import React, { useState, useContext, useEffect } from 'react';
import { withAuthorization } from '../Session';
import { withFirebase } from '../Firebase';
import { AuthUserContext } from '../Session';
import { getDashboardRouteForEmail } from '../../utils/dashboardRouting';
import ConfirmModal from '../ConfirmModal';
import { useNotificationPreferences } from '../../lib/notifications/notificationsHooks';
import './styles.css';

const SettingsPageBase = ({ firebase, history }) => {
  const authUser = useContext(AuthUserContext);
  const [activeTab, setActiveTab] = useState('notifications');
  const getInitialSettings = () => ({
    emailNotifications: true,
    weeklyReports: true,
    darkMode: localStorage.getItem('darkMode') === 'true',
    language: 'en',
    timezone: 'Asia/Kolkata',
    currency: 'INR',
    dateFormat: 'DD/MM/YYYY',
  });

  const [settings, setSettings] = useState(getInitialSettings());
  const [originalSettings, setOriginalSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [allowNavigation, setAllowNavigation] = useState(false);
  const dashboardRoute = getDashboardRouteForEmail(authUser?.email);

  const notificationCategories = [
    { key: 'SYSTEM', label: 'System', description: 'Maintenance, updates, and platform notices.' },
    { key: 'BILLING', label: 'Billing', description: 'Invoices, payments, and subscription status.' },
    { key: 'PRODUCT', label: 'Product', description: 'Feature announcements and releases.' },
    { key: 'OPERATIONAL', label: 'Operational', description: 'Approvals, tickets, and ops updates.' },
    { key: 'MARKETING', label: 'Marketing', description: 'Campaigns and promotional content.' },
    { key: 'SECURITY', label: 'Security', description: 'Critical security alerts (always on).', locked: true },
  ];

  const notificationChannels = [
    { key: 'in_app', label: 'In-app' },
    { key: 'email', label: 'Email' },
  ];

  const {
    preferences: notificationPreferences,
    loading: notificationPreferencesLoading,
    error: notificationPreferencesError,
    savePreference: saveNotificationPreference,
  } = useNotificationPreferences(firebase);

  // Check if there are unsaved changes
  const hasUnsavedChanges = originalSettings && JSON.stringify(settings) !== JSON.stringify(originalSettings);

  // Debug: Log when unsaved changes state changes
  useEffect(() => {
    if (originalSettings) {
      console.log('Settings changed:', hasUnsavedChanges);
      console.log('Current settings:', settings);
      console.log('Original settings:', originalSettings);
    }
  }, [hasUnsavedChanges, settings, originalSettings]);

  // Load saved settings (with defaults fallback) on mount
  useEffect(() => {
    if (!authUser) {
      return;
    }

    const loadSettings = async () => {
      const defaults = getInitialSettings();
      try {
        const userDoc = await firebase.db.collection('users').doc(authUser.uid).get();
        const savedSettings = userDoc.exists && userDoc.data()?.settings ? userDoc.data().settings : {};
        const mergedSettings = {
          ...defaults,
          ...savedSettings,
        };

        setSettings(mergedSettings);
        setOriginalSettings(JSON.parse(JSON.stringify(mergedSettings)));

        if (mergedSettings.darkMode) {
          document.body.classList.add('dark-mode');
        } else {
          document.body.classList.remove('dark-mode');
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        setSettings(defaults);
        setOriginalSettings(JSON.parse(JSON.stringify(defaults)));
        if (defaults.darkMode) {
          document.body.classList.add('dark-mode');
        } else {
          document.body.classList.remove('dark-mode');
        }
      }
    };

    loadSettings();
  }, [authUser, firebase]);

  // Prompt user before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges && !allowNavigation) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Do you want to save them?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges, allowNavigation]);

  const handleToggle = (setting) => {
    const newValue = !settings[setting];
    
    setSettings(prev => ({
      ...prev,
      [setting]: newValue
    }));

    // Special handling for dark mode - apply class immediately for preview
    if (setting === 'darkMode') {
      if (newValue) {
        document.body.classList.add('dark-mode');
      } else {
        document.body.classList.remove('dark-mode');
      }
      // Don't save to localStorage yet - wait for user to click Save
    }
  };

  const handleSelect = (setting, value) => {
    setSettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const findNotificationPreference = (category, channel) =>
    notificationPreferences.find((pref) => pref.category === category && pref.channel === channel);

  const isNotificationEnabled = (category, channel) => {
    if (category === 'SECURITY') return true;
    const pref = findNotificationPreference(category, channel);
    if (!pref) return true;
    return pref.enabled !== false;
  };

  const handleNotificationToggle = async (category, channel) => {
    const nextEnabled = !isNotificationEnabled(category, channel);
    await saveNotificationPreference({ category, channel, enabled: nextEnabled });
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccessMessage('');
    
    try {
      // Save dark mode to localStorage
      localStorage.setItem('darkMode', settings.darkMode.toString());
      
      await firebase.db
        .collection('users')
        .doc(authUser.uid)
        .set(
          {
            settings: settings,
            updatedAt: firebase.fieldValue.serverTimestamp(),
          },
          { merge: true }
        );
      
      // Update originalSettings to match current settings after successful save
      // Deep copy to ensure proper change detection
      setOriginalSettings(JSON.parse(JSON.stringify(settings)));
      
      setSuccessMessage('Settings saved successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
      
      return true; // Return success indicator
    } catch (error) {
      console.error('Error saving settings:', error);
      alert('Failed to save settings');
      return false; // Return failure indicator
    } finally {
      setSaving(false);
    }
  };

  const handleBackToDashboard = () => {
    if (hasUnsavedChanges) {
      setShowConfirmModal(true);
    } else {
      window.location.href = dashboardRoute;
    }
  };

  const handleConfirmModalClose = () => {
    setShowConfirmModal(false);
  };

  const handleConfirmModalConfirm = async () => {
    // Save and then navigate
    setShowConfirmModal(false); // Close modal first
    const saved = await handleSave();
    
    if (saved) {
      console.log('Settings saved successfully, navigating to dashboard');
      setAllowNavigation(true);
      setTimeout(() => {
        window.location.href = dashboardRoute;
      }, 300); // Slightly longer delay to ensure state updates
    } else {
      console.error('Failed to save settings');
      setAllowNavigation(false);
    }
  };

  const handleCancelAndNavigate = () => {
    // Revert dark mode to original setting
    if (originalSettings && originalSettings.darkMode !== settings.darkMode) {
      if (originalSettings.darkMode) {
        document.body.classList.add('dark-mode');
      } else {
        document.body.classList.remove('dark-mode');
      }
    }
    
    // Navigate without saving
    setAllowNavigation(true);
    setTimeout(() => {
      window.location.href = dashboardRoute;
    }, 100);
  };

  return (
    <div className="settings-page">
      <div className="settings-container">
        <div className="settings-header">
          <div className="settings-header-content">
            <h1>Settings</h1>
            <p>Manage your account preferences and application settings</p>
          </div>
          <button 
            className="btn-back-dashboard"
            onClick={handleBackToDashboard}
          >
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Dashboard
          </button>
        </div>

        <div className="settings-content">
          <div className="settings-tabs">
            <button 
              className={`settings-tab ${activeTab === 'notifications' ? 'active' : ''}`}
              onClick={() => setActiveTab('notifications')}
            >
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              Notifications
            </button>
            <button 
              className={`settings-tab ${activeTab === 'preferences' ? 'active' : ''}`}
              onClick={() => setActiveTab('preferences')}
            >
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
              Preferences
            </button>
            <button 
              className={`settings-tab ${activeTab === 'privacy' ? 'active' : ''}`}
              onClick={() => setActiveTab('privacy')}
            >
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Privacy & Security
            </button>
          </div>

          <div className="settings-panels">
            {activeTab === 'notifications' && (
              <div className="settings-panel">
                <h2>Notification Preferences</h2>
                
                <div className="setting-section">
                  <h3>Email Notifications</h3>
                  
                  <div className="setting-item">
                    <div className="setting-info">
                      <label>All Email Notifications</label>
                      <p>Receive email notifications for important updates</p>
                    </div>
                    <label className="toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={settings.emailNotifications}
                        onChange={() => handleToggle('emailNotifications')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  <div className="setting-item">
                    <div className="setting-info">
                      <label>Weekly updates & blogs</label>
                      <p>Get weekly updates and new blog posts</p>
                    </div>
                    <label className="toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={settings.weeklyReports}
                        onChange={() => handleToggle('weeklyReports')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>

                <div className="setting-section">
                  <h3>On-site alerts</h3>
                  <p style={{ marginTop: 0, color: 'rgba(107,114,128,0.9)' }}>You’ll see welcome messages and updates in the notification bell.</p>
                </div>
                <div className="setting-section">
                  <h3>Category Controls</h3>
                  <p style={{ marginTop: 0, color: 'rgba(107,114,128,0.9)' }}>
                    Manage which categories you receive in-app or email notifications for.
                  </p>
                  {notificationPreferencesError && (
                    <p style={{ color: '#b91c1c', marginTop: '0.5rem' }}>
                      Failed to load notification preferences.
                    </p>
                  )}
                  {notificationPreferencesLoading ? (
                    <p style={{ marginTop: '0.5rem' }}>Loading preferences...</p>
                  ) : (
                    notificationCategories.map((category) => (
                      <div key={category.key} className="setting-item">
                        <div className="setting-info">
                          <label>{category.label}</label>
                          <p>{category.description}</p>
                        </div>
                        <div className="notification-channel-toggles">
                          {notificationChannels.map((channel) => {
                            const isEnabled = isNotificationEnabled(category.key, channel.key);
                            return (
                              <div key={channel.key} className="channel-toggle">
                                <label className="toggle-switch">
                                  <input
                                    type="checkbox"
                                    checked={isEnabled}
                                    disabled={category.locked}
                                    onChange={() => handleNotificationToggle(category.key, channel.key)}
                                  />
                                  <span className="toggle-slider"></span>
                                </label>
                                <span className="toggle-label">{channel.label}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {activeTab === 'preferences' && (
              <div className="settings-panel">
                <h2>Application Preferences</h2>
                
                <div className="setting-section">
                  <h3>Display Settings</h3>
                  
                  <div className="setting-item">
                    <div className="setting-info">
                      <label>Dark Mode</label>
                      <p>Switch between light and dark theme</p>
                    </div>
                    <label className="toggle-switch">
                      <input 
                        type="checkbox" 
                        checked={settings.darkMode}
                        onChange={() => handleToggle('darkMode')}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  {false && (
                  <div className="setting-item">
                    <div className="setting-info">
                      <label>Language</label>
                      <p>Choose your preferred language</p>
                    </div>
                    <select 
                      className="setting-select"
                      value={settings.language}
                      onChange={(e) => handleSelect('language', e.target.value)}
                    >
                      <option value="en">English</option>
                      <option value="hi">हिन्दी (Hindi)</option>
                      <option value="mr">मराठी (Marathi)</option>
                      <option value="ta">தமிழ் (Tamil)</option>
                      <option value="te">తెలుగు (Telugu)</option>
                      <option value="kn">ಕನ್ನಡ (Kannada)</option>
                    </select>
                  </div>
                  )}
                </div>

                <div className="setting-section">
                  <h3>Regional Settings</h3>
                  
                  <div className="setting-item">
                    <div className="setting-info">
                      <label>Timezone</label>
                      <p>Set your local timezone</p>
                    </div>
                    <select 
                      className="setting-select"
                      value={settings.timezone}
                      onChange={(e) => handleSelect('timezone', e.target.value)}
                    >
                      <option value="Asia/Kolkata">Asia/Kolkata (IST)</option>
                      <option value="UTC">UTC</option>
                      <option value="America/New_York">America/New York (EST)</option>
                      <option value="Europe/London">Europe/London (GMT)</option>
                      <option value="Asia/Dubai">Asia/Dubai (GST)</option>
                      <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
                    </select>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <label>Currency</label>
                      <p>Choose your preferred currency</p>
                    </div>
                    <select 
                      className="setting-select"
                      value={settings.currency}
                      onChange={(e) => handleSelect('currency', e.target.value)}
                    >
                      <option value="INR">INR (₹)</option>
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (€)</option>
                      <option value="GBP">GBP (£)</option>
                      <option value="AED">AED (د.إ)</option>
                    </select>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <label>Date Format</label>
                      <p>Choose how dates are displayed</p>
                    </div>
                    <select 
                      className="setting-select"
                      value={settings.dateFormat}
                      onChange={(e) => handleSelect('dateFormat', e.target.value)}
                    >
                      <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                      <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'privacy' && (
              <div className="settings-panel">
                <h2>Privacy & Security</h2>
                
                <div className="setting-section">
                  <h3>Account Security</h3>
                  
                  <div className="setting-item">
                    <div className="setting-info">
                      <label>Password</label>
                      <p>Manage your account password</p>
                    </div>
                    <button 
                      className="setting-action-btn"
                      onClick={() => window.location.href = '/password-change'}
                    >
                      Change Password
                    </button>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <label>Active Sessions</label>
                      <p>Manage your active login sessions</p>
                    </div>
                    <button className="setting-action-btn">
                      View Sessions
                    </button>
                  </div>
                </div>

                <div className="setting-section">
                  <h3>Data & Privacy</h3>
                  
                  <div className="setting-item">
                    <div className="setting-info">
                      <label>Download Your Data</label>
                      <p>Request a copy of your personal data</p>
                    </div>
                    <button className="setting-action-btn">
                      Request Data
                    </button>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <label>Privacy Policy</label>
                      <p>Review our privacy policy</p>
                    </div>
                    <button 
                      className="setting-action-btn"
                      onClick={() => window.open('/privacy', '_blank')}
                    >
                      View Policy
                    </button>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <label>Terms & Conditions</label>
                      <p>Review our terms of service</p>
                    </div>
                    <button 
                      className="setting-action-btn"
                      onClick={() => window.open('/terms', '_blank')}
                    >
                      View Terms
                    </button>
                  </div>

                  <div className="setting-item">
                    <div className="setting-info">
                      <label>Delete Account</label>
                      <p className="text-danger">Permanently delete your account and data</p>
                    </div>
                    <button className="setting-action-btn danger">
                      Delete Account
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {successMessage && (
            <div className="success-banner">
              {successMessage}
            </div>
          )}

          <div className="settings-footer">
            <button 
              className="btn-save-settings"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={handleConfirmModalClose}
        onConfirm={handleConfirmModalConfirm}
        onCancel={handleCancelAndNavigate}
        title="Unsaved Changes"
        message="You have unsaved changes. Do you want to save your settings before leaving?"
        confirmText="Save & Leave"
        cancelText="Leave Without Saving"
      />
    </div>
  );
};

const condition = authUser => !!authUser;

const SettingsPage = withFirebase(withAuthorization(condition)(SettingsPageBase));

export default SettingsPage;


